import {
  AdminApiError,
  type AdminClient,
  type GreedykingJackpotOverride,
  type JackpotSnapshot,
} from '@apx/admin-sdk';
import { prettyGame } from '../games.js';
import { greedykingJackpotSchema } from '@apx/integrator-config/schemas';
import { icon } from '../icons.js';

// Greedyking-only page. Sicbo → sicbo-jackpot.ts (dispatched by gameId in
// game-detail.ts); dragontiger has no jackpot pool.
type JackpotOverride = GreedykingJackpotOverride;
const jackpotSchema = greedykingJackpotSchema;
import { askConfirm, askPrompt } from '../modal.js';
import { fmtDateTime, fmtMoney, fmtNumber } from '../format.js';
import { escapeHtml } from './reports-shared.js';
import { showToast } from '../toast.js';

export interface JackpotPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
}

// Mirrors DEFAULT_JACKPOT_CONFIG in @apx/game-greedyking's jackpot.ts — the
// REAL per-category model the running engine reads. Recalibrated
// (maxMultBps 2.0×, maxTriggerRateBps 3%) to keep worst-case food RTP ≈ 95%;
// the values that used to live here (5.0×, 15%) were stale from before that
// recalibration and never matched what round-engine actually computed.
const LINEAR_DEFAULTS = {
  seed: '100000',
  threshold: '1000000',
  baseMultBps: 12_500, // 1.25x
  maxMultBps: 20_000, // 2.0x
  maxTriggerRateBps: 300, // 3% at full pool
  rakeBps: 200, // 2%
};

// Tier1/tier2/reseed/bonusMultiplier are DEPRECATED — the
// orchestrator no longer reads them (see `JackpotConfig`'s `@deprecated`
// JSDoc in @apx/game-greedyking's jackpot.ts). Kept here only so the
// collapsed "Legacy" section below has something to show/edit for older
// stored overrides; schema made them optional.
const DEFAULT_VALUE: JackpotOverride = {
  tier1Threshold: '50000000',
  tier1Probability: 0.05,
  tier2Threshold: '100000000',
  tier2Probability: 0.15,
  poolReseed: '10000000',
  poolContributionRate: 0.05,
  bonusMultiplier: 2.0,
  veg: { ...LINEAR_DEFAULTS },
  meat: { ...LINEAR_DEFAULTS },
};

const STALE_THRESHOLD_HOURS = 72;

export async function renderJackpotPage(root: HTMLElement, deps: JackpotPageDeps): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Jackpot Config — ${prettyGame(deps.gameId)}</h1>
    <p class="page-sub">Two independent pools — veg feeds the SALAD trigger, meat feeds PIZZA. Each pool's special-tier multiplier and per-round fire chance scale linearly with its own fill (1.25x at seed → maxMult at threshold; 0% fire chance at seed → maxTriggerRate at threshold). The greedyking iframe's <code>JackpotPill</code> reads <code>saladMultiplier</code>/<code>pizzaMultiplier</code> from the public <code>/v1/jackpot/{game}</code> snapshot — this is the config that actually drives it.</p>

    <div class="card">
      <h2>Configuration</h2>
      <div class="form-grid" style="grid-template-columns:repeat(2,1fr);gap:18px;">
        <fieldset style="border:1px solid var(--border);padding:10px;border-radius:6px">
          <legend>Veg pool (feeds SALAD trigger)</legend>
          <div class="form-grid">
            <label>seed (credits)
              <input id="jp-veg-seed" type="number" min="0" step="10000" />
            </label>
            <label>threshold (credits)
              <input id="jp-veg-threshold" type="number" min="1" step="100000" />
            </label>
            <label>baseMult (bps, 12500 = 1.25x)
              <input id="jp-veg-base-mult-bps" type="number" min="0" max="1000000" step="100" />
            </label>
            <label>maxMult (bps, 20000 = 2.00x)
              <input id="jp-veg-max-mult-bps" type="number" min="0" max="1000000" step="100" />
            </label>
            <label>maxTriggerRate (bps, 300 = 3%)
              <input id="jp-veg-max-trigger-bps" type="number" min="0" max="10000" step="50" />
            </label>
            <label>rake (bps, 200 = 2%)
              <input id="jp-veg-rake-bps" type="number" min="0" max="10000" step="25" />
            </label>
          </div>
        </fieldset>
        <fieldset style="border:1px solid var(--border);padding:10px;border-radius:6px">
          <legend>Meat pool (feeds PIZZA trigger)</legend>
          <div class="form-grid">
            <label>seed (credits)
              <input id="jp-meat-seed" type="number" min="0" step="10000" />
            </label>
            <label>threshold (credits)
              <input id="jp-meat-threshold" type="number" min="1" step="100000" />
            </label>
            <label>baseMult (bps)
              <input id="jp-meat-base-mult-bps" type="number" min="0" max="1000000" step="100" />
            </label>
            <label>maxMult (bps)
              <input id="jp-meat-max-mult-bps" type="number" min="0" max="1000000" step="100" />
            </label>
            <label>maxTriggerRate (bps)
              <input id="jp-meat-max-trigger-bps" type="number" min="0" max="10000" step="50" />
            </label>
            <label>rake (bps)
              <input id="jp-meat-rake-bps" type="number" min="0" max="10000" step="25" />
            </label>
          </div>
        </fieldset>
      </div>
      <div class="row">
        <button id="jp-save" class="primary">Propose change</button>
        <button id="jp-reset-defaults" class="ghost">Reset to defaults</button>
        <button id="jp-force-reseed" class="ghost">Force pool reseed</button>
        <button id="jp-solver" class="ghost">Win-rate solver</button>
        <span id="jp-msg" class="muted"></span>
      </div>
    </div>

    <details class="card" id="jp-legacy-card">
      <summary style="cursor:pointer;font-weight:600">Legacy fields (inert) — not used by the running engine ▸</summary>
      <p class="muted" style="font-size:12px;margin:8px 0">
        Tier thresholds/probabilities, pool reseed, the flat bonus multiplier, and the contribution-rate split below are
        <strong>dead config</strong> — replaced 2026-05-29 by the per-category pools above (<code>veg.rakeBps</code>/<code>meat.rakeBps</code>
        for contribution, <code>computeDynamicMult</code> for payout, <code>computeTriggerProbability</code> for fire chance).
        They're editable here only for continuity with older stored overrides; changing them has <strong>no effect</strong> on gameplay.
      </p>
      <div class="form-grid">
        <label>Tier 1 threshold <span class="muted">(inert)</span>
          <input id="jp-t1-thresh" type="number" min="1" step="100000" />
        </label>
        <label>Tier 1 fire probability (0..1) <span class="muted">(inert)</span>
          <input id="jp-t1-prob" type="number" min="0" max="1" step="0.01" />
        </label>
        <label>Tier 2 threshold <span class="muted">(inert)</span>
          <input id="jp-t2-thresh" type="number" min="1" step="100000" />
        </label>
        <label>Tier 2 fire probability <span class="muted">(inert)</span>
          <input id="jp-t2-prob" type="number" min="0" max="1" step="0.01" />
        </label>
        <label>Pool reseed amount <span class="muted">(inert)</span>
          <input id="jp-reseed" type="number" min="0" step="100000" />
        </label>
        <label>Pool contribution rate — scalar (0..1) <span class="muted">(inert)</span>
          <input id="jp-rate" type="number" min="0" max="1" step="0.005" />
        </label>
        <label>Special-tier bonus multiplier (0..10) <span class="muted">(inert)</span>
          <input id="jp-bonus-mult" type="number" min="0" max="10" step="0.1" />
        </label>
      </div>
      <div style="border-top:1px dashed var(--border);margin:14px 0;padding-top:10px">
        <label style="display:inline-flex;align-items:center;gap:6px">
          <input id="jp-split-enabled" type="checkbox" /> Use per-category contribution split <span class="muted">(inert)</span>
        </label>
        <p class="muted" style="margin:6px 0 8px;font-size:12px">Superseded by <code>veg.rakeBps</code> / <code>meat.rakeBps</code> above — has no effect even when checked.</p>
        <div class="form-grid">
          <label>Veg contribution rate
            <input id="jp-split-veg" type="number" min="0" max="1" step="0.005" disabled />
          </label>
          <label>Meat contribution rate
            <input id="jp-split-meat" type="number" min="0" max="1" step="0.005" disabled />
          </label>
        </div>
      </div>
    </details>

    <div class="card" id="jp-stale-card" hidden>
      <h2 style="color:var(--accent-amber);margin-top:0;display:inline-flex;align-items:center;gap:7px">${icon('warn', { size: 18 })}Stale pool alert</h2>
      <p id="jp-stale-body" class="muted"></p>
    </div>

    <div class="card">
      <h2>Expected hit cadence</h2>
      <p class="muted" style="margin-top:-4px;font-size:12px">Naive forecast assuming ~22s rounds + the current form's per-category thresholds/trigger rates against the LIVE pool sizes. Forecast lands within ±30% in practice — use it to sanity-check the operator-facing knobs before publishing.</p>
      <div id="jp-rtp-card"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Live pool snapshot</h2>
      <div id="jp-live"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Recent jackpot wins</h2>
      <p class="muted" style="margin-top:-4px;font-size:12px">
        Pulled from <code>JACKPOT_PAYOUT</code> wallet calls. Each row is a
        confirmed payout to the qualifying bet with the largest stake. Pool
        decremented atomically against current balance per round.
      </p>
      <div class="row" style="margin-bottom:8px;align-items:center">
        <label>Window
          <select id="jp-drains-since">
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d" selected>Last 30 days</option>
            <option value="all">All time</option>
          </select>
        </label>
        <button id="jp-drains-refresh" class="ghost">Refresh</button>
      </div>
      <div id="jp-drains"><p class="muted">Loading…</p></div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="jp-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const inputs = {
    t1Thresh: root.querySelector<HTMLInputElement>('#jp-t1-thresh')!,
    t1Prob: root.querySelector<HTMLInputElement>('#jp-t1-prob')!,
    t2Thresh: root.querySelector<HTMLInputElement>('#jp-t2-thresh')!,
    t2Prob: root.querySelector<HTMLInputElement>('#jp-t2-prob')!,
    reseed: root.querySelector<HTMLInputElement>('#jp-reseed')!,
    rate: root.querySelector<HTMLInputElement>('#jp-rate')!,
    bonusMult: root.querySelector<HTMLInputElement>('#jp-bonus-mult')!,
    splitEnabled: root.querySelector<HTMLInputElement>('#jp-split-enabled')!,
    splitVeg: root.querySelector<HTMLInputElement>('#jp-split-veg')!,
    splitMeat: root.querySelector<HTMLInputElement>('#jp-split-meat')!,
    // Primary per-category linear pool inputs — always live (promoted
    // from an opt-in toggle to the primary editing surface, since
    // this is the only config shape the running engine reads).
    vegSeed: root.querySelector<HTMLInputElement>('#jp-veg-seed')!,
    vegThreshold: root.querySelector<HTMLInputElement>('#jp-veg-threshold')!,
    vegBaseMultBps: root.querySelector<HTMLInputElement>('#jp-veg-base-mult-bps')!,
    vegMaxMultBps: root.querySelector<HTMLInputElement>('#jp-veg-max-mult-bps')!,
    vegMaxTriggerBps: root.querySelector<HTMLInputElement>('#jp-veg-max-trigger-bps')!,
    vegRakeBps: root.querySelector<HTMLInputElement>('#jp-veg-rake-bps')!,
    meatSeed: root.querySelector<HTMLInputElement>('#jp-meat-seed')!,
    meatThreshold: root.querySelector<HTMLInputElement>('#jp-meat-threshold')!,
    meatBaseMultBps: root.querySelector<HTMLInputElement>('#jp-meat-base-mult-bps')!,
    meatMaxMultBps: root.querySelector<HTMLInputElement>('#jp-meat-max-mult-bps')!,
    meatMaxTriggerBps: root.querySelector<HTMLInputElement>('#jp-meat-max-trigger-bps')!,
    meatRakeBps: root.querySelector<HTMLInputElement>('#jp-meat-rake-bps')!,
  };
  const msgEl = root.querySelector<HTMLElement>('#jp-msg')!;
  const liveEl = root.querySelector<HTMLElement>('#jp-live')!;
  const staleCardEl = root.querySelector<HTMLElement>('#jp-stale-card')!;
  const staleBodyEl = root.querySelector<HTMLElement>('#jp-stale-body')!;
  const rtpCardEl = root.querySelector<HTMLElement>('#jp-rtp-card')!;

  inputs.splitEnabled.addEventListener('change', () => {
    inputs.splitVeg.disabled = !inputs.splitEnabled.checked;
    inputs.splitMeat.disabled = !inputs.splitEnabled.checked;
  });

  function fillForm(v: JackpotOverride): void {
    // Legacy (inert) fields.
    inputs.t1Thresh.value = v.tier1Threshold ?? DEFAULT_VALUE.tier1Threshold!;
    inputs.t1Prob.value = String(v.tier1Probability ?? DEFAULT_VALUE.tier1Probability);
    inputs.t2Thresh.value = v.tier2Threshold ?? DEFAULT_VALUE.tier2Threshold!;
    inputs.t2Prob.value = String(v.tier2Probability ?? DEFAULT_VALUE.tier2Probability);
    inputs.reseed.value = v.poolReseed ?? DEFAULT_VALUE.poolReseed!;
    inputs.rate.value = String(v.poolContributionRate ?? DEFAULT_VALUE.poolContributionRate);
    inputs.bonusMult.value = String(v.bonusMultiplier ?? 2.0);
    const split = v.poolContributionRateByCategory;
    inputs.splitEnabled.checked = !!split;
    inputs.splitVeg.disabled = !split;
    inputs.splitMeat.disabled = !split;
    inputs.splitVeg.value = String(split?.veg ?? 0.03);
    inputs.splitMeat.value = String(split?.meat ?? 0.08);

    // Primary per-category linear fields — fall back to the real engine
    // defaults (LINEAR_DEFAULTS) when a pre-Phase-C override never set them.
    const veg = v.veg ?? LINEAR_DEFAULTS;
    const meat = v.meat ?? LINEAR_DEFAULTS;
    inputs.vegSeed.value = veg.seed;
    inputs.vegThreshold.value = veg.threshold;
    inputs.vegBaseMultBps.value = String(veg.baseMultBps);
    inputs.vegMaxMultBps.value = String(veg.maxMultBps);
    inputs.vegMaxTriggerBps.value = String(veg.maxTriggerRateBps);
    inputs.vegRakeBps.value = String(veg.rakeBps);
    inputs.meatSeed.value = meat.seed;
    inputs.meatThreshold.value = meat.threshold;
    inputs.meatBaseMultBps.value = String(meat.baseMultBps);
    inputs.meatMaxMultBps.value = String(meat.maxMultBps);
    inputs.meatMaxTriggerBps.value = String(meat.maxTriggerRateBps);
    inputs.meatRakeBps.value = String(meat.rakeBps);
  }

  function readForm(): JackpotOverride {
    const base: JackpotOverride = {
      // Legacy (inert) — still submitted for continuity with existing
      // stored overrides; the running engine ignores all of these.
      tier1Threshold: inputs.t1Thresh.value,
      tier1Probability: Number(inputs.t1Prob.value),
      tier2Threshold: inputs.t2Thresh.value,
      tier2Probability: Number(inputs.t2Prob.value),
      poolReseed: inputs.reseed.value,
      poolContributionRate: Number(inputs.rate.value),
      bonusMultiplier: Number(inputs.bonusMult.value),
      // Primary — this is what round-engine actually reads.
      veg: {
        seed: inputs.vegSeed.value || LINEAR_DEFAULTS.seed,
        threshold: inputs.vegThreshold.value || LINEAR_DEFAULTS.threshold,
        baseMultBps: Number(inputs.vegBaseMultBps.value),
        maxMultBps: Number(inputs.vegMaxMultBps.value),
        maxTriggerRateBps: Number(inputs.vegMaxTriggerBps.value),
        rakeBps: Number(inputs.vegRakeBps.value),
      },
      meat: {
        seed: inputs.meatSeed.value || LINEAR_DEFAULTS.seed,
        threshold: inputs.meatThreshold.value || LINEAR_DEFAULTS.threshold,
        baseMultBps: Number(inputs.meatBaseMultBps.value),
        maxMultBps: Number(inputs.meatMaxMultBps.value),
        maxTriggerRateBps: Number(inputs.meatMaxTriggerBps.value),
        rakeBps: Number(inputs.meatRakeBps.value),
      },
    };
    if (inputs.splitEnabled.checked) {
      base.poolContributionRateByCategory = {
        veg: Number(inputs.splitVeg.value),
        meat: Number(inputs.splitMeat.value),
      };
    }
    return base;
  }

  /** SALAD/PIZZA roll independently each round against their own pool's
   *  fill (see `computeTriggerProbability` in @apx/game-greedyking's
   *  jackpot.ts — linear from 0% at seed to maxTriggerRateBps at
   *  threshold). "Any special fires" is the union of the two. */
  function categoryTriggerProb(pool: number, threshold: number, maxTriggerRateBps: number): number {
    if (threshold <= 0) return 0;
    const fillPct = Math.max(0, Math.min(1, pool / threshold));
    return fillPct * (maxTriggerRateBps / 10_000);
  }

  function renderRtpCard(snap: JackpotSnapshot): void {
    if (snap.model !== 'category') {
      rtpCardEl.innerHTML = '<p class="muted">Live pool data unavailable.</p>';
      return;
    }
    // Preview the CURRENT form values (possibly unsaved) against the live
    // pool sizes — same "sanity-check before publishing" intent as before.
    const v = readForm();
    const vegPool = Number(snap.veg.pool);
    const meatPool = Number(snap.meat.pool);
    const vegThreshold = Number(v.veg?.threshold || snap.veg.threshold);
    const meatThreshold = Number(v.meat?.threshold || snap.meat.threshold);
    const vegMaxTriggerBps = v.veg?.maxTriggerRateBps ?? snap.veg.maxTriggerRateBps;
    const meatMaxTriggerBps = v.meat?.maxTriggerRateBps ?? snap.meat.maxTriggerRateBps;
    const pVeg = categoryTriggerProb(vegPool, vegThreshold, vegMaxTriggerBps);
    const pMeat = categoryTriggerProb(meatPool, meatThreshold, meatMaxTriggerBps);
    const pAny = 1 - (1 - pVeg) * (1 - pMeat);
    const roundsToHit = pAny > 0 ? 1 / pAny : Infinity;
    const ROUND_SECONDS = 22;
    const hours = ((roundsToHit * ROUND_SECONDS) / 3600).toFixed(2);
    const colour =
      pAny === 0
        ? 'var(--text-dim)'
        : pAny >= 0.1
          ? 'var(--accent-green)'
          : pAny >= 0.02
            ? 'var(--accent-amber)'
            : 'var(--accent-red)';
    rtpCardEl.innerHTML = `
      <div class="kv">
        <dt>SALAD (veg) fire chance</dt><dd>${(pVeg * 100).toFixed(2)}%</dd>
        <dt>PIZZA (meat) fire chance</dt><dd>${(pMeat * 100).toFixed(2)}%</dd>
        <dt>Any special fire chance</dt><dd style="color:${colour};font-weight:600">${(pAny * 100).toFixed(2)}%</dd>
        <dt>~ rounds until next hit</dt><dd>${pAny > 0 ? roundsToHit.toFixed(0) : '∞'}</dd>
        <dt>~ wall-clock until next hit</dt><dd>${pAny > 0 ? `${hours} h` : 'never (0% max trigger rate)'}</dd>
        <dt>Veg pool</dt><dd>${fmtMoney(snap.veg.pool, snap.currency ?? 'IDR')} ${escapeHtml(snap.currency ?? 'IDR')}</dd>
        <dt>Meat pool</dt><dd>${fmtMoney(snap.meat.pool, snap.currency ?? 'IDR')} ${escapeHtml(snap.currency ?? 'IDR')}</dd>
      </div>
    `;
  }

  function renderStaleAlert(drains: Array<{ settledAt?: string }>): void {
    const latest = drains[0]?.settledAt ? new Date(drains[0].settledAt) : null;
    if (!latest) {
      staleCardEl.hidden = true;
      return;
    }
    const ageMs = Date.now() - latest.getTime();
    const ageHours = ageMs / 3_600_000;
    if (ageHours < STALE_THRESHOLD_HOURS) {
      staleCardEl.hidden = true;
      return;
    }
    staleCardEl.hidden = false;
    staleBodyEl.textContent = `Last jackpot drain ${ageHours.toFixed(1)} h ago (${latest.toISOString()}). Exceeds the ${STALE_THRESHOLD_HOURS} h alert window — verify maxTriggerRateBps is high enough for either pool, or consider Force pool reseed.`;
  }

  function renderLive(snap: JackpotSnapshot): void {
    if (snap.model !== 'category') {
      liveEl.innerHTML = '<p class="muted">Live pool data unavailable.</p>';
      return;
    }
    // The pool's currency: its balance, and the threshold it fills towards,
    // are integer minor units of it. A snapshot without one predates the
    // per-currency pools and is the IDR pool.
    const cur = snap.currency ?? 'IDR';
    liveEl.innerHTML = `
      <div class="kv">
        <dt>Pool currency</dt><dd><code>${escapeHtml(cur)}</code></dd>
        <dt>Veg pool (SALAD)</dt><dd style="color:var(--accent);font-size:20px;font-weight:600">${fmtMoney(snap.veg.pool, cur)}</dd>
        <dt>Veg fill</dt><dd>${(snap.veg.fillPct * 100).toFixed(1)}% (towards ${fmtMoney(snap.veg.threshold, cur)})</dd>
        <dt>Meat pool (PIZZA)</dt><dd style="color:var(--accent);font-size:20px;font-weight:600">${fmtMoney(snap.meat.pool, cur)}</dd>
        <dt>Meat fill</dt><dd>${(snap.meat.fillPct * 100).toFixed(1)}% (towards ${fmtMoney(snap.meat.threshold, cur)})</dd>
        <dt>Salad multiplier</dt><dd>${snap.saladMultiplier}x</dd>
        <dt>Pizza multiplier</dt><dd>${snap.pizzaMultiplier}x</dd>
        <dt>Veg rake</dt><dd>${(snap.veg.rakeBps / 100).toFixed(2)}% of losing veg bets</dd>
        <dt>Meat rake</dt><dd>${(snap.meat.rakeBps / 100).toFixed(2)}% of losing meat bets</dd>
      </div>
    `;
  }

  async function refreshLive(): Promise<void> {
    try {
      const snap = await deps.client.fetchJackpot(deps.roundEngineBaseUrl, deps.integratorId, deps.gameId);
      renderLive(snap);
      renderRtpCard(snap);
    } catch (err) {
      liveEl.innerHTML = `<p class="error">${
        err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)
      }</p>`;
      rtpCardEl.innerHTML = `<p class="error">snapshot unavailable</p>`;
    }
  }

  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'jackpot');
  fillForm((current as JackpotOverride | null) ?? DEFAULT_VALUE);

  root.querySelector<HTMLButtonElement>('#jp-reset-defaults')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    msgEl.textContent = 'Reset to defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#jp-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = jackpotSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => e.message).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(
        deps.integratorId,
        deps.gameId,
        'jackpot',
        parsed.data,
      );
      msgEl.textContent = `Pending approval ${res.approval.id}.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  root.querySelector<HTMLButtonElement>('#jp-force-reseed')!.addEventListener('click', async () => {
    if (!(await askConfirm('Force pool reseed? Resets pool counter to configured poolReseed amount.'))) return;
    try {
      const res = await deps.client.forceReseedJackpot(deps.integratorId, deps.gameId);
      const summary = res.pools
        .map((p) => `${p.poolId} ${fmtMoney(p.previous, res.currency)}→${fmtMoney(p.pool, res.currency)}`)
        .join(', ');
      msgEl.textContent = `Pool reseeded (${res.currency}): ${summary}.`;
      msgEl.className = 'muted';
      await refreshLive();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'jackpot' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#jp-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'jackpot');
          fillForm((fresh.value as JackpotOverride | null) ?? DEFAULT_VALUE);
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          showToast(err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err), 'error');
        }
      });
    });
  }

  async function refreshDrains(): Promise<void> {
    const since = root.querySelector<HTMLSelectElement>('#jp-drains-since')!.value as
      | '24h'
      | '7d'
      | '30d'
      | 'all';
    const host = root.querySelector<HTMLElement>('#jp-drains')!;
    host.innerHTML = '<p class="muted">Loading…</p>';
    try {
      const res = await deps.client.fetchJackpotDrains(deps.gameId, { since, limit: 50 });
      renderStaleAlert(res.drains.map((d) => ({ settledAt: d.createdAt })));
      if (res.drains.length === 0) {
        host.innerHTML = '<p class="muted">No jackpot drains in this window. Pool either never tripped a win-trigger or just reseeded.</p>';
        return;
      }
      host.innerHTML = `<div style="max-height:42vh;overflow:auto"><table>
        <thead><tr>
          <th>When</th>
          <th>Player</th>
          <th>Round</th>
          <th>Integrator</th>
          <th>Currency</th>
          <th style="text-align:right">Amount</th>
          <th style="text-align:right">Fiat</th>
          <th>Status</th>
        </tr></thead>
        <tbody>${res.drains
          .map(
            (d) => `<tr>
              <td>${fmtDateTime(d.createdAt)}</td>
              <td><a href="/integrators/${encodeURIComponent(d.integratorId)}/players/${d.playerId}" style="color:var(--accent-2);text-decoration:none"><code style="font-size:11px">${d.playerId.slice(0, 8)}…</code></a></td>
              <td>${d.roundId ? `<code style="font-size:11px">${d.roundId.slice(-10)}</code>` : '<span class="muted">—</span>'}</td>
              <td><code style="font-size:11px">${d.integratorId.slice(0, 8)}…</code></td>
              <td>${d.currency ? `<code>${escapeHtml(d.currency)}</code>` : '<span class="muted">—</span>'}</td>
              <td style="text-align:right;font-variant-numeric:tabular-nums;color:var(--success);font-weight:600">+${fmtMoney(d.creditAmount, d.currency)}</td>
              <td style="text-align:right;font-variant-numeric:tabular-nums">${d.fiatAmount ? `${fmtMoney(d.fiatAmount, d.fiatCurrency)} ${escapeHtml(d.fiatCurrency ?? '')}` : '<span class="muted">—</span>'}</td>
              <td><span class="pill ${d.status === 'confirmed' ? 'live' : 'warn'}">${d.status}</span></td>
            </tr>`,
          )
          .join('')}</tbody>
      </table></div>`;
    } catch (err) {
      host.innerHTML = `<p class="error">${err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err)}</p>`;
    }
  }

  root.querySelector<HTMLButtonElement>('#jp-drains-refresh')!.addEventListener('click', () => void refreshDrains());
  root.querySelector<HTMLSelectElement>('#jp-drains-since')!.addEventListener('change', () => void refreshDrains());

  // Win-rate solver for the per-category
  // linear model. At steady state (pool sitting at/above threshold) the
  // trigger probability saturates at `maxTriggerRateBps` (see
  // `computeTriggerProbability` in @apx/game-greedyking's jackpot.ts) —
  // solve for the bps that lands close to the target cadence, per
  // category. Naive model, assumes 22s rounds.
  root.querySelector<HTMLButtonElement>('#jp-solver')!.addEventListener('click', async () => {
    const targetStr = await askPrompt('Target wall-clock hours between hits (per category, at a full pool)?', '2');
    if (!targetStr) return;
    const targetHours = Number(targetStr);
    if (!isFinite(targetHours) || targetHours <= 0) {
      showToast('Enter a positive number of hours.', 'error');
      return;
    }
    const ROUND_SECONDS = 22;
    const targetRounds = (targetHours * 3600) / ROUND_SECONDS;
    const recommendedMaxTriggerBps = Math.min(10_000, Math.max(1, Math.round(10_000 / targetRounds)));
    const currentSnap = await deps.client
      .fetchJackpot(deps.roundEngineBaseUrl, deps.integratorId, deps.gameId)
      .catch(() => null);
    const thresholdStr =
      currentSnap?.model === 'category' ? currentSnap.veg.threshold : LINEAR_DEFAULTS.threshold;
    const threshold = Number(thresholdStr);
    const avgHouseTakePerRound = 50_000; // operator-tunable; rough average
    const recommendedRakeBps = Math.min(
      10_000,
      Math.max(1, Math.round((threshold / (targetRounds * avgHouseTakePerRound)) * 10_000)),
    );
    showToast(
      `Solver (per category, at full pool): maxTriggerRateBps ≈ ${recommendedMaxTriggerBps} (${(recommendedMaxTriggerBps / 100).toFixed(2)}%), ` +
        `rakeBps ≈ ${recommendedRakeBps} (${(recommendedRakeBps / 100).toFixed(2)}%). ` +
        `(based on ~22s rounds, ${avgHouseTakePerRound.toLocaleString()}/round avg house take, threshold ${fmtNumber(thresholdStr)})`,
      'info',
    );
  });

  await refreshLive();
  await refreshPending();
  await refreshDrains();
  const liveTimer = setInterval(() => void refreshLive(), 5000);

  const cleanup = new MutationObserver(() => {
    if (!root.querySelector('#jp-live')) {
      clearInterval(liveTimer);
      cleanup.disconnect();
    }
  });
  cleanup.observe(root, { childList: true });
}
